<template>
  <iDropdown
    :show-dropdown="showFontFamilyPicker"
    :teleport-to-body="false"
    :width="width"
    :overlay-position="myOverlayPosition"
    @close="showFontFamilyPicker = false"
  >
    <template #trigger>
      <iColumn :id="myIndicatorId" @click="showFontFamilyPicker = true">
        <slot name="trigger">
          <iColumn gap="none">
            <iLabel v-if="label">
              {{ label }}
            </iLabel>
            <iRow
              class="trigger-display"
              :style-overrides="{
                paddingLeft: 'small',
                paddingRight: 'small',
                borderWidth: 'standard',
                borderColor: 'dark',
                borderRadius: 'large',
                paddingTop: 'extraSmall',
                paddingBottom: 'extraSmall',
                backgroundColor: 'paper',
                hoverBackgroundColor: 'subtle',
              }"
              align="between"
              wrap="nowrap"
              width="fill"
            >
              <iText
                font-size="small"
                :variant="internalModelValue.value ? 'standard' : 'subtle'"
                :style="`fontFamily: ${internalModelValue.value || 'nunito'}`"
              >
                {{ optionLabel }}
              </iText>
              <iText
                v-if="aliasLabel"
                font-size="small"
                variant="subtle"
              >
                {{ aliasLabel }}
              </iText>
            </iRow>
          </iColumn>
        </slot>
      </iColumn>
    </template>
    <template #overlay>
      <iColumn
        v-drag="'#color-picker-header'"
        class="color-picker-body-wrapper"
        gap="none"
        :width="300"
        :style-overrides="{
          backgroundColor: 'paper',
          borderRadius: 'large',
          borderWidth: 'thin',
          borderColor: 'dark'
        }"
      >
        <iRow
          id="color-picker-header"
          class="header"
          position="relative"
          vertical-align="middle"
          align="center"
          :style-overrides="{
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall'
          }"
        >
          <iButton
            class="close-modal-button"
            size="extraSmall"
            variant="tertiary"
            text-display="inline-flex"
            :style-overrides="{
              extraSmallPaddingTop: 'none',
              extraSmallPaddingBottom: 'none',
              extraSmallPaddingLeft: 'none',
              extraSmallPaddingRight: 'none',
            }"
            @click.stop="resetAndClose"
          >
            <iIcon icon="close" size="small" />
          </iButton>
          <iText font-size="large">
            Fonts
          </iText>
        </iRow>
        <iDivider />
        <iRow
          v-if="fontFamilyPickerTypes.length > 1"
          class="navigation"
          align="between"
          vertical-align="middle"
          :style-overrides="{
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            paddingLeft: 'standard',
            paddingRight: 'standard',
          }"
        >
          <iButton
            class="previous-panel-btn"
            size="extraSmall"
            variant="text"
            :class="{
              'button-with-icon-left': currentFontFamilyPickerTypeIndex - 1 >= 0
            }"
            @click="goToPreviousPanel"
          >
            <iRow
              vertical-align="middle"
              width="hug"
              gap="extraSmall"
              wrap="nowrap"
            >
              <iIcon
                v-if="currentFontFamilyPickerTypeIndex - 1 >= 0"
                icon="caret-left"
                variant="subtle"
              />
              <iText :variant="!previousFontFamilyPickerType ? 'subtle' : 'standard'">
                {{
                  previousFontFamilyPickerType ? previousFontFamilyPickerType.label : "TypeFace"
                }}
              </iText>
            </iRow>
          </iButton>
          <iButton
            class="next-panel-btn"
            size="extraSmall"
            variant="text"
            :class="{
              'button-with-icon-right': currentFontFamilyPickerTypeIndex + 1 < fontFamilyPickerTypes.length
            }"
            @click="goToNextPanel"
          >
            <iRow
              vertical-align="middle"
              width="hug"
              gap="extraSmall"
              wrap="nowrap"
            >
              <iText :variant="!nextFontFamilyPickerType ? 'subtle' : 'standard'">
                {{ !nextFontFamilyPickerType ? "Raw" : nextFontFamilyPickerType.label }}
              </iText>
              <iIcon
                v-if="currentFontFamilyPickerTypeIndex + 1 < fontFamilyPickerTypes.length"
                icon="caret-right"
                variant="subtle"
              />
            </iRow>
          </iButton>
        </iRow>
        <iDivider v-if="fontFamilyPickerTypes.length > 1" />
        <iRow
          v-if="fontFamilyColumnLabel && tempType !== 'custom'"
          class="column-headers"
          align="between"
          :style-overrides="{
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            paddingLeft: 'standard',
            paddingRight: 'standard',
          }"
          vertical-align="middle"
          wrap="nowrap"
        >
          <iColumn>
            <iText>
              Alias
            </iText>
          </iColumn>
          <iColumn :align="fontFamilyColumnLabel === 'Variant' ? 'center' : 'right'">
            <iText>
              {{ fontFamilyColumnLabel }}
            </iText>
          </iColumn>
        </iRow>
        <iColumn class="color-container-wrappers" :style-overrides="{paddingBottom: 'extraSmall'}">
          <iColumn v-if="tempType==='theme'" gap="none">
            <iRow
              v-for="(themeFont, index) in themeFontList"
              :key="index"
              align="between"
              vertical-align="middle"
              width="fill"
              class="list-item"
              :class="{
                'selected-item': themeItemIsSelected(themeFont)
              }"
              :style-overrides="{
                paddingLeft: 'standard',
                paddingRight: 'standard',
                paddingTop: 'small',
                paddingBottom: 'small',
              }"
              @click="selectValue(themeFont)"
            >
              <iText
                font-size="small"
                :style="`fontFamily: ${themeFont.value}`"
              >
                {{
                  themeFont.label
                }}
              </iText>
              <iText
                font-size="small"
                :variant="themeItemIsSelected(themeFont) ? 'standard' : 'subtle'"
              >
                {{
                  camelToTitleCase(themeFont.value)
                }}
              </iText>
            </iRow>
          </iColumn>
          <iColumn
            v-if="tempType==='custom'"
            :style-overrides="{
              paddingLeft: 'small',
              paddingTop: 'small',
              paddingBottom: 'small',
              paddingRight: 'small',
            }"
          >
            <iTextInput
              v-model="searchFontsQuery"
              size="small"
              placeholder="Search fonts.."
              width="fill"
              focus
              :right-icon="searchFontsQuery && !loadingSearchFonts ? 'close' : ''"
              :is-loading="loadingSearchFonts"
              @click:icon-right="searchFontsQuery = ''"
            />

            <iColumn
              gap="none"
              overflow="auto"
              :max-height="300"
              wrap="nowrap"
            >
              <iButton
                v-for="(fontItem, index) in fonts"
                :key="index"
                :variant="internalModelValue.value === fontItem.value ? 'primary' : 'tertiary'"
                width="fill"
                size="extraSmall"
                @click="selectValue(fontItem)"
              >
                <iText
                  :style="`fontFamily: ${fontItem.value}`"
                  :variant="internalModelValue.value === fontItem.value ? 'light' : 'standard'"
                >
                  {{
                    fontItem.label
                  }}
                </iText>
              </iButton>
            </iColumn>
          </iColumn>
        </iColumn>
      </iColumn>
    </template>
  </iDropdown>
</template>

<script>
import { camelToTitleCase } from "@bloglovin/vue-framework";
import { mapState, mapWritableState, mapActions } from "pinia";
import { useStyleStore, useFontStore, Enum } from "@vcl";
import { useStagingFrameStore } from "~/stores/staging-frame-store";
import { useGeneralStore } from "%/stores/general-store";
import { links } from "%/enums/link-enums";
import { uniqueId } from "lodash";
import basicThemePalette from "%/themes/basic-theme-palette";

export default {
  name: "FontFamilyPickerV2",
  props: {
    modelValue: {
      type: [Object, String],
      default: () => ({}),
    },
    hideThemePicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideCustomPicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    width: {
      type: [Enum, Number],
      required: false,
      default: "fill",
      options: ["hug", "fill"],
    },
    label: {
      type: String,
      default: "",
    },
    popupPosition: {
      type: Enum,
      required: false,
      default: "default",
      options: ["bottom", "default"],
    },
  },
  emits: ["update:model-value"],
  data() {
    return {
      searchFontsQuery: "",
      fontSearchDebounce: null,
      showFontFamilyPicker: false,
      internalModelValue: {},
      tempType: "theme",
      myIndicatorId: uniqueId("font-family-indicator-"),
      myOverlayPositionY: 0,
      myOverlayPositionX: 0,
      types: [
        { label: "Theme", key: "theme" },
        { label: "Raw", key: "custom" },
      ],
    };
  },
  computed: {
    ...mapState(useStyleStore, ["themes"]),
    ...mapWritableState(useFontStore, ["fonts", "loadingSearchFonts"]),
    myOverlayPosition() {
      return `${this.myOverlayPositionY}-${this.myOverlayPositionX}`;
    },
    apiV1() {
      return useGeneralStore().getLink(links.api);
    },
    publicationThemeId() {
      return useStagingFrameStore().themeId;
    },
    myTheme() {
      return this.themes[this.publicationThemeId];
    },
    themeFontList() {
      return Object.keys(this.myTheme.font.typeface).reduce((fontList, font) => {
        const extractedValueRegexMatches = this.myTheme.font.typeface[font].match(/\['([^']+)'\]/);
        if (!extractedValueRegexMatches) {
          return fontList;
        }
        fontList.push({
          label: camelToTitleCase(font),
          value: extractedValueRegexMatches[1],
          source: this.isInspirationSource(extractedValueRegexMatches[1]) ? "inspiration" : "google", // need to rework
          context: "theme",
        });
        return fontList;
      }, []);
    },
    fontFamilyPickerTypes() {
      return this.types.filter(type => {
        if (type.key === "theme" && this.hideThemePicker) {
          return false;
        }
        if (type.key === "custom" && this.hideCustomPicker) {
          return false;
        }
        return true;
      });
    },
    fontFamilyColumnLabel() {
      const currentFontFamilyPanelKey = this.fontFamilyPickerTypes[this.currentFontFamilyPickerTypeIndex].key;
      switch (currentFontFamilyPanelKey) {
        case "theme":
          return "Theme Value";
        case "custom":
          return "Raw";
        default:
          return "";
      }
    },
    currentFontFamilyPickerTypeIndex() {
      return this.fontFamilyPickerTypes.findIndex(({ key }) => key.includes(this.tempType));
    },
    previousFontFamilyPickerType() {
      return this.fontFamilyPickerTypes[this.currentFontFamilyPickerTypeIndex - 1];
    },
    nextFontFamilyPickerType() {
      return this.fontFamilyPickerTypes[this.currentFontFamilyPickerTypeIndex + 1];
    },
    optionLabel() {
      if (this.internalModelValue.context === "custom" && !this.hideThemePicker) {
        return "Custom";
      }

      return this.internalModelValue.label || "Select a font family";
    },
    aliasLabel() {
      if (this.hideThemePicker) {
        return;
      }
      return camelToTitleCase(this.internalModelValue.value);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(value) {
        if (typeof value !== "string") {
          this.tempType = value.context;
        } else {
          this.types.forEach(type => {
            if (type.key === "theme" && !this.hideThemePicker) {
              this.tempType = type.key;
              return;
            }
            if (type.key === "custom" && !this.hideCustomPicker) {
              this.tempType = type.key;
            }
          });
        }
        this.internalModelValue = {
          "label": typeof value === "string" ? camelToTitleCase(value) : camelToTitleCase(value.label),
          "source": typeof value === "string" ? "inspiration" : value.source,
          "value": typeof value === "string" ? value : value.value,
          "context": this.tempType,
        };

        if (this.isInspirationSource(this.internalModelValue.value)) {
          this.internalModelValue.source = "inspiration";
        }

        // support for previous structure
        const themeItem = this.themeFontList.find(themeFont => themeFont.label.toLowerCase() ===
            this.internalModelValue.value.toLowerCase());
        if (themeItem) {
          this.internalModelValue.value = themeItem.value;
          this.internalModelValue.context = "theme";
          this.tempType = "theme";
        }

        // Re-resolve the theme alias to its current typeface so a remap shows without a re-select.
        if (this.internalModelValue.context === "theme" && this.internalModelValue.label) {
          const aliasKey = this.internalModelValue.label.replaceAll(" ", "").toLowerCase();
          const currentThemeFont = this.themeFontList.find(
            themeFont => themeFont.label.replaceAll(" ", "").toLowerCase() === aliasKey,
          );
          if (currentThemeFont) {
            this.internalModelValue.value = currentThemeFont.value;
          }
        }

        if (this.internalModelValue.context === "theme") {
          this.includeInitialFont({
            ...this.internalModelValue,
            label: camelToTitleCase(this.internalModelValue.value),
          });
        } else {
          this.includeInitialFont(this.internalModelValue);
        }
      },
    },
    showFontFamilyPicker() {
      let left = window.innerWidth - 305;
      left -= useStagingFrameStore().rightPanelWidth;

      let top = 0;
      const dropdownIndicatorElement = document.querySelector(`#${this.myIndicatorId}`);
      if (dropdownIndicatorElement) {
        const dropdownIndicatorElementRect = dropdownIndicatorElement.getBoundingClientRect();
        top = dropdownIndicatorElementRect.bottom;
        if (this.popupPosition === "bottom") {
          left = dropdownIndicatorElementRect.left;
        }
      }

      this.myOverlayPositionY = top;
      this.myOverlayPositionX = left;
    },
    fonts() {
      this.loadFontsToBody();
    },
    searchFontsQuery(value) {
      if (this.fontSearchDebounce) {
        clearTimeout(this.fontSearchDebounce);
      }
      this.loadingSearchFonts = true;
      this.fontSearchDebounce = setTimeout(() => {
        this.searchFonts(value);
      }, 500);
    },
  },
  methods: {
    ...mapActions(useFontStore, [
      "searchForFonts",
      "loadFontsToBody",
      "includeInitialFont",
    ]),
    camelToTitleCase,
    isInspirationSource(fontFamily) {
      return Object.values(basicThemePalette.fonts.typeface).find(typeface => {
        return typeface.replaceAll(" ", "").toLowerCase() === fontFamily.replaceAll(" ", "").toLowerCase();
      });
    },
    themeItemIsSelected(themeFont) {
      const internalValue = this.internalModelValue.label.replaceAll(" ", "").toLowerCase();
      const comparisonValue = themeFont.label.replaceAll(" ", "").toLowerCase();
      return internalValue === comparisonValue;
    },
    async searchFonts(value) {
      this.loadingSearchFonts = false;
      this.searchForFonts(
        value,
        this.apiV1 + "/fonts/search",
        this.internalModelValue,
      );
    },
    selectValue(value) {
      this.internalModelValue = value;
      this.$emit("update:model-value", {
        "label": camelToTitleCase(value.label),
        "source": this.isInspirationSource(value.value) ? "inspiration" : value.source,
        "value": value.value,
        "context": this.tempType,
        "url": value.url,
      });
      this.showFontFamilyPicker = false;
    },
    goToPreviousPanel() {
      if (this.currentFontFamilyPickerTypeIndex - 1 >= 0) {
        this.tempType = this.previousFontFamilyPickerType.key;
      }
    },
    goToNextPanel() {
      if (this.currentFontFamilyPickerTypeIndex + 1 < this.fontFamilyPickerTypes.length) {
        this.tempType = this.nextFontFamilyPickerType.key;
      }
    },
    resetAndClose() {
      this.$emit("update:model-value", this.internalModelValue);
      this.showFontFamilyPicker = false;
    },
  },
  styleGuide: () => ({
    hoverBackgroundColor: { "color.background": "light" },
    selectedBackgroundColor: { "color.border": "striking" },
  }),
};
</script>

<style scoped lang="scss">
.close-modal-button {
  position: absolute;
  left: 8px;
  top: 8px;
  padding: 2px !important;
}

.button-with-icon-left {
  position: relative;
  right: 6px;
}

.button-with-icon-right {
  position: relative;
  left: 6px;
}

:deep(.i-dropdown-content) {
  box-shadow: -2px 3px 6px grey;
}

.list-item {

  &:hover {
    cursor: pointer;
    background-color: v-bind('$getStyles.hoverBackgroundColor');
  }

  &.selected-item {
    background-color: v-bind('$getStyles.selectedBackgroundColor');
  }
}
</style>
